import React, { useCallback, useMemo } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { AppView } from 'design-system/AppView';
import Spacer from 'design-system/Spacer';
import Button from 'design-system/Button';
import { CardView } from 'design-system/CardView';
import { SettingsItem } from 'design-system/SettingsItem';
import { SettingsSection } from 'design-system/SettingsSection';
import Squircle from 'design-system/Squircle';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import { TextButtonLinkSeeAll } from 'design-system/TextButton';
import { IconBank, IconHome, IconUserPlus } from 'design-system/icons';
import IconCreditScore from 'design-system/icons/IconCreditScore';
import { IconEmma } from 'design-system/icons/IconEmma';
import IconInvest from 'design-system/icons/IconInvest';
import IconPercent from 'design-system/icons/IconPercent';
import { IconRocket } from 'design-system/icons/IconRocket';
import IconTransfer from 'design-system/icons/IconTransfer';
import { rem, SCREEN_BOTTOM_PADDING } from 'design-system/values';
import { UpgradeWebModal } from 'design-system/UpgradeWebModal';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { selectFilteredBenefitsDataForTierWithCustomAndTopFeatures, selectShouldSeePremiumTrialScreen, selectIsOnReverseTrial, selectEligibleFreeTrialDays, selectShowSubscriptionPaymentFailed, selectShowSeatBasedSubscriptionPaymentFailed, selectTryFor0Text, DEFAULT_UPGRADE_TEXT, } from 'features/premium/selectors';
import { useMyLimits } from 'features/settings/plan-overview/hooks/useMyLimits';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppSelector } from 'store/hooks';
import { benefits, tierThemes } from 'features/premium/benefits';
import { Tier, isPremiumTier, getNextTier, tierGreaterOrEqualThan, } from 'features/premium/entities';
import { selectUserOrganisation, selectUserTier, } from 'features/premium/selectors/tier';
import useOpenManageAppStoreSubscription from 'features/premium/hooks/useOpenManageAppStoreSubscription';
import useStripePayment from 'features/premium/hooks/useStripePayment';
import { formatRentReportingAgencies } from 'features/rent-reporting/utils';
import TierBenefit from 'features/premium/components/TierBenefit';
import SeatBasedSubscriptionPaymentFailed from 'features/feed/components/needs-fix/SeatBasedSubscriptionPaymentFailed';
import SubscriptionPaymentFailed from 'features/feed/components/needs-fix/SubscriptionPaymentFailed';
import { formatAmount, formatDateForItem, formatTryFor0, isUnlocked, } from 'utils';
import { getEmmaIconColorProps } from 'utils/colors';
import SwitchToYearlyButton from 'features/premium/components/SwitchToYearlyButton';
import AnimatedWiggleCard from 'features/settings/plan-overview/components/AnimatedWiggleCard';
import { UnavailableOnWebModal } from 'utils/packages/UnavailableOnWebModal';
import { selectDefaultCurrency } from 'reducers/selectors';
import DiscountBanner from 'features/promotions/components/DiscountBanner';
import i18n from 'utils/i18n';
import { isWeb } from '../../../constants';
const strings = {
    headerTitle: 'Your plan',
    bankTransfers: 'Bank Transfers',
    interest: 'Interest on your savings',
    connections: 'Bank connections',
    fxFees: 'Fx Fee on your trades',
    aumFees: 'AUM fee on your assets',
    autosaveFees: 'Autosave fee',
    autoInvestFees: 'Autoinvest fee',
    extraMember: 'Extra member',
    upgrade: 'Upgrade',
    yourLimits: 'Your limits',
};
const HEADER_HEIGHT = rem(110);
export const PlanOverviewScreen = withAnimatedNavigationHeader(({ navigation, onScroll }) => {
    const { limitsText, userInterest, userFxFees, userAUMFees, autoInvestFees, userAutosaveFees, userRentReportingAgencies, userExtraMembers, userConnectionsLimit, userCreditScore, } = useMyLimits();
    const colors = useColors();
    const userTier = useAppSelector(selectUserTier);
    const emmaProStatus = useAppSelector((store) => store.user.user.emmaProStatus);
    const reverseTrialExpiresOn = useAppSelector((store) => store.user.user.emmaProStatus.reverseTrial?.expiresOn);
    const isOnReverseTrial = useAppSelector(selectIsOnReverseTrial);
    const reverseTrial = isOnReverseTrial && reverseTrialExpiresOn;
    const showSubscriptionPaymentFailed = useAppSelector(selectShowSubscriptionPaymentFailed);
    const showSeatBasedSubscriptionPaymentFailed = useAppSelector(selectShowSeatBasedSubscriptionPaymentFailed);
    const { reactivateStripeSubscriptionFn, fetching } = useStripePayment();
    const manageSubscription = useOpenManageAppStoreSubscription();
    const benefitsData = useAppSelector((store) => selectFilteredBenefitsDataForTierWithCustomAndTopFeatures(store, userTier));
    const topFeatures = useMemo(() => benefitsData.find((section) => section.name === 'Top features'), [benefitsData]);
    const emmaIconColorProps = useMemo(() => getEmmaIconColorProps(userTier), [userTier]);
    const items = useMemo(() => {
        const array = [];
        if (userCreditScore) {
            array.push({
                title: userCreditScore.title,
                value: userCreditScore.description,
                icon: <IconCreditScore gradientKey="apricot"/>,
            });
        }
        if (userConnectionsLimit) {
            array.push({
                icon: <IconBank gradientKey="darkPink"/>,
                title: strings.connections,
                value: userConnectionsLimit,
            });
        }
        if (userInterest) {
            array.push({
                icon: <IconPercent gradientKey="apricot"/>,
                title: strings.interest,
                value: userInterest,
            });
        }
        if (userAutosaveFees) {
            array.push({
                value: userAutosaveFees,
                title: strings.autosaveFees,
                icon: <IconRocket colorKey="save"/>,
            });
        }
        if (autoInvestFees) {
            array.push({
                value: autoInvestFees,
                title: strings.autoInvestFees,
                icon: <IconRocket colorKey="investment"/>,
            });
        }
        if (userAUMFees) {
            array.push({
                value: userAUMFees,
                title: strings.aumFees,
                icon: <IconInvest gradientKey="apricot"/>,
            });
        }
        if (userFxFees) {
            array.push({
                value: userFxFees,
                title: strings.fxFees,
                icon: <IconInvest gradientKey="darkPurple"/>,
            });
        }
        if (limitsText) {
            array.push({
                title: strings.bankTransfers,
                value: limitsText,
                icon: (<IconTransfer gradientColors={colors.gradients.ultimate.colors}/>),
            });
        }
        if (userRentReportingAgencies) {
            const agencies = userRentReportingAgencies[userTier];
            array.push({
                title: 'Rent reporting',
                value: formatRentReportingAgencies(agencies) || 'Not available',
                icon: <IconHome colorKey="statusInformational"/>,
            });
        }
        if (userExtraMembers) {
            array.push({
                title: strings.extraMember,
                value: userExtraMembers,
                icon: <IconUserPlus colorKey="brand"/>,
            });
        }
        return array;
    }, [
        userConnectionsLimit,
        userInterest,
        userAutosaveFees,
        autoInvestFees,
        userAUMFees,
        userFxFees,
        limitsText,
        userRentReportingAgencies,
        userExtraMembers,
        userCreditScore,
        colors.gradients.ultimate.colors,
        userTier,
    ]);
    const employer = useAppSelector(selectUserOrganisation);
    const planType = 'type' in emmaProStatus && emmaProStatus.type;
    const isInEmployerPlan = planType === 'employee';
    const isInFamilyPlan = planType === 'family';
    const showAutoRenewButton = useMemo(() => !isWeb &&
        emmaProStatus.active &&
        !emmaProStatus.autoRenewing &&
        !isOnReverseTrial &&
        !isInEmployerPlan &&
        !isInFamilyPlan &&
        (emmaProStatus.store === 'stripe' ||
            emmaProStatus.store === 'apple' ||
            emmaProStatus.store === 'google'), [emmaProStatus, isOnReverseTrial, isInEmployerPlan, isInFamilyPlan]);
    const onPressEnableAutoRenew = useCallback(() => {
        if (emmaProStatus.active && isPremiumTier(emmaProStatus.tier)) {
            if (emmaProStatus.store === 'stripe') {
                reactivateStripeSubscriptionFn();
            }
            else {
                manageSubscription();
            }
        }
    }, [
        emmaProStatus.active,
        emmaProStatus.store,
        emmaProStatus.tier,
        manageSubscription,
        reactivateStripeSubscriptionFn,
    ]);
    const getPlanDescription = () => {
        if ('type' in emmaProStatus &&
            emmaProStatus.type === 'employee' &&
            employer) {
            return `Licence provided by ${employer.companyName}`;
        }
        if ('type' in emmaProStatus && emmaProStatus.type === 'family') {
            return 'Extra member';
        }
        if (userTier === Tier.free) {
            return 'Upgrade your plan to unlock premium features and boost your limits';
        }
        const period = reverseTrial || emmaProStatus.isOnFreeTrial
            ? 'Free trial'
            : (emmaProStatus.period?.charAt(0).toUpperCase() ?? '') +
                (emmaProStatus.period?.slice(1) ?? '-');
        const renewalDate = emmaProStatus.expiresOn
            ? formatDateForItem(emmaProStatus.expiresOn)
            : '-';
        const renewalText = emmaProStatus.autoRenewing
            ? `Renews on ${renewalDate}`
            : `Expires on ${renewalDate}`;
        const renewalPeriod = emmaProStatus.autoRenewPeriod;
        const currentPeriod = emmaProStatus.period;
        const isTierDowngrade = !!emmaProStatus.autoRenewTier &&
            !tierGreaterOrEqualThan(emmaProStatus.autoRenewTier, userTier);
        if (isTierDowngrade && emmaProStatus.autoRenewTier) {
            return `Downgrades to ${tierThemes[emmaProStatus.autoRenewTier].name} on ${renewalDate}`;
        }
        const isChangingPeriod = renewalPeriod && currentPeriod && renewalPeriod !== currentPeriod;
        if (isChangingPeriod) {
            return `${period} • Renews to ${renewalPeriod} on ${renewalDate}`;
        }
        return `${period} • ${renewalText}`;
    };
    const nextTier = useMemo(() => getNextTier(userTier), [userTier]);
    const handleUpgradeBtnPress = useCallback(() => {
        if (isWeb) {
            UpgradeWebModal.show(userTier);
        }
        else {
            navigation.navigateDeprecated('EmmaProModal', { tier: nextTier });
        }
    }, [nextTier, userTier]);
    const handleManageBtnPress = useCallback(() => {
        if (isWeb) {
            UnavailableOnWebModal.show('emma://manage-plan', 'Manage plan');
        }
        else {
            navigation.navigateDeprecated('ManagePremiumSubscription');
        }
    }, []);
    const onPressSeeAll = useCallback(() => {
        if (isWeb) {
            UnavailableOnWebModal.show('emma://upgrade/true/ULTIMATE?currentAndAbovePlan=true', 'See all of your features');
        }
        else {
            navigation.navigateDeprecated('EmmaProModal', {
                skipTrialScreen: true,
                currentAndAbovePlan: true,
            });
        }
    }, []);
    const renderTopFeaturesTitle = useCallback(() => (<View style={styles.sectionTitleContainer}>
          <Text Gray Text-16>
            Your top features
          </Text>
          <TextButtonLinkSeeAll onPress={onPressSeeAll}/>
        </View>), [onPressSeeAll]);
    const renderYourLimitsTitle = useCallback(() => (<View style={styles.sectionTitleContainer}>
          <Text Gray Text-16>
            {strings.yourLimits}
          </Text>
        </View>), []);
    const isNordVPNAvailable = isUnlocked(userTier, benefits.nordVPN.minTier);
    const isEligibleForFreeTrial = useAppSelector((store) => selectShouldSeePremiumTrialScreen(store, userTier));
    const eligibleFreeTrialDays = useAppSelector(selectEligibleFreeTrialDays);
    const tryFor0Text = useAppSelector(selectTryFor0Text);
    const userCurrency = useAppSelector(selectDefaultCurrency);
    const upgradeButtonText = useMemo(() => {
        if (isEligibleForFreeTrial && eligibleFreeTrialDays) {
            if (tryFor0Text && tryFor0Text !== DEFAULT_UPGRADE_TEXT) {
                return formatTryFor0(userCurrency, 'Upgrade');
            }
            return `Start ${eligibleFreeTrialDays}-day free trial`;
        }
        return strings.upgrade;
    }, [
        tryFor0Text,
        isEligibleForFreeTrial,
        eligibleFreeTrialDays,
        userCurrency,
    ]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottomStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
        <ScrollView onScroll={onScroll} contentContainerStyle={paddingBottomStyle} scrollEventThrottle={16}>
          <StatusBar barStyle="dark-content" animated/>
          {showSubscriptionPaymentFailed ? (<SubscriptionPaymentFailed noMarginBottom={!showSeatBasedSubscriptionPaymentFailed} style={styles.failedPaymentBanner}/>) : null}
          {showSeatBasedSubscriptionPaymentFailed ? (<SeatBasedSubscriptionPaymentFailed noMarginBottom style={!showSubscriptionPaymentFailed && styles.failedPaymentBanner}/>) : null}
          <DiscountBanner hideClose cardViewProps={{ noMarginTop: false }}/>
          <CardView style={styles.planDescriptionContainer} noMarginBottom>
            <View style={styles.planDescriptionTextandIconContainer}>
              <View style={styles.planDescriptionTextContainer}>
                <Text Text-16 numberOfLines={1} ellipsizeMode="tail">
                  {tierThemes[userTier].name}
                </Text>
                <Text TextThin-14 Secondary numberOfLines={2} ellipsizeMode="tail">
                  {getPlanDescription()}
                </Text>
              </View>
              <View style={styles.iconContainer}>
                <Squircle color={colors.background.dark}>
                  <IconEmma {...emmaIconColorProps} height={24} width={24}/>
                </Squircle>
              </View>
            </View>
            <View>
              {!!showAutoRenewButton && (<Button flex square errorReversed testID="enableAutoRenewButton" isFetching={fetching} containerStyle={styles.enableAutoRenewButton} onPress={onPressEnableAutoRenew} title={'Enable auto-renew'}/>)}
              <View style={[
            styles.planDescriptionButtonsContainer,
            !!isEligibleForFreeTrial &&
                styles.planDescriptionButtonsContainerCol,
        ]}>
                {userTier !== Tier.ultimate && (<Button brand square flex title={upgradeButtonText} containerStyle={styles.upgradeButton} onPress={handleUpgradeBtnPress}/>)}

                <Button brandReversed square flex title="Manage" containerStyle={styles.upgradeButton} onPress={handleManageBtnPress}/>
              </View>
              <SwitchToYearlyButton outline extraStyle={styles.switchToYearlyButton}/>
            </View>
          </CardView>
          {!!isEligibleForFreeTrial && (<AnimatedWiggleCard title="Start free trial today!" description={`Unlock all premium features and try them out, completely free for ${eligibleFreeTrialDays} days!`} delay={1200}/>)}
          {!!emmaProStatus.isOnFreeTrial &&
            !!emmaProStatus.currentFreeTrialDays &&
            !!nextTier && (<AnimatedWiggleCard color={{ brand: true }} title={`Why not ${i18n.t('maximise')} your trial?`} description={`During your ${emmaProStatus.currentFreeTrialDays}-day free trial, you can explore a higher tier plan with no commitment. Unlock ${nextTier === Tier.ultimate ? 'the full experience' : 'it'} now!`} buttonText={`Try ${tierThemes[nextTier].name} for ${formatAmount(0, emmaProStatus.currentPrice?.currency || userCurrency, 2)}`} onPress={handleUpgradeBtnPress} delay={1200}/>)}
          <SettingsSection renderHeader={renderYourLimitsTitle}>
            {items.map((item) => (<SettingsItem key={item.title} title={item.title} description={item.value} icon={item.icon}/>))}
          </SettingsSection>
          <SettingsSection renderHeader={renderTopFeaturesTitle}>
            {topFeatures?.data
            ?.filter((feature) => isNordVPNAvailable ? true : feature.id !== 'nordVPN')
            ?.map((feature) => (<TierBenefit key={feature.id} customName={feature.customData?.customName} customDescription={feature.customData?.customDescription} customIcon={feature.customData?.customIcon} item={feature} disabled/>))}
          </SettingsSection>
        </ScrollView>
        {isWeb ? <Spacer h={24}/> : null}
      </AppView>);
}, ({ colors }) => ({
    title: strings.headerTitle,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    planDescriptionContainer: {
        padding: rem(16),
        gap: rem(24),
    },
    planDescriptionTextandIconContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    planDescriptionButtonsContainer: {
        flexDirection: 'row',
        gap: rem(8),
    },
    planDescriptionButtonsContainerCol: {
        flexDirection: 'column',
    },
    planDescriptionTextContainer: {
        flex: 1,
        marginRight: rem(16),
    },
    iconContainer: {
        flexShrink: 0,
    },
    upgradeButton: {
        height: rem(34),
    },
    enableAutoRenewButton: {
        height: rem(34),
        marginBottom: rem(12),
    },
    indicator: {
        alignSelf: 'center',
        marginBottom: HEADER_HEIGHT,
    },
    sectionTitleContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingBottom: rem(16),
    },
    switchToYearlyButton: {
        marginTop: rem(12),
    },
    failedPaymentBanner: {
        marginTop: rem(24),
    },
});
